import { useMutation, useQuery, useQueryClient, type QueryClient, type QueryKey, type UseQueryResult } from '@tanstack/react-query';
import type {
  FailureCause,
  FailureGroups,
  MessageAudience,
  MessageDeliveryDetail,
  MessageDeliveryRow,
  MessageDeliveryState,
  MessageLogFilter,
  MessageLogPage,
  ReaddressResult,
  ResendAllResult,
} from '@nest-recruit/shared';
import { ApiError, apiGet, apiPost } from '../../../lib/api-client';
import { MESSAGE_LOG_KEY } from '../../notifications/api/notifications-api';
import { ADMIN_MENU_COUNTS_KEY } from './overview-api';

// ADMIN Email delivery (Admin redesign batch 2, Option A). Everything sits under the log's key, so the test send,
// every action and the reconnect refetch refresh the list, the pills, the cause cards and the open email together.

/** Long enough not to matter, short enough that "Still trying" turns into Sent or Failed while the page is open. */
const REFRESH_MS = 20_000;

export interface MessageLogParams {
  readonly state?: MessageLogFilter;
  readonly q?: string;
  readonly audience?: MessageAudience;
  readonly from?: string;
  readonly to?: string;
  readonly page: number;
}

export function useMessageLog(params: MessageLogParams, enabled: boolean): UseQueryResult<MessageLogPage, ApiError> {
  return useQuery<MessageLogPage, ApiError>({
    queryKey: [...MESSAGE_LOG_KEY, 'list', params],
    queryFn: () =>
      apiGet<MessageLogPage>('/notifications/messages', {
        query: {
          page: params.page,
          ...(params.state ? { state: params.state } : {}),
          ...(params.q ? { q: params.q } : {}),
          ...(params.audience ? { audience: params.audience } : {}),
          ...(params.from ? { from: params.from } : {}),
          ...(params.to ? { to: params.to } : {}),
        },
      }),
    placeholderData: (previous) => previous,
    refetchInterval: REFRESH_MS,
    enabled,
  });
}

export function useFailureGroups(enabled: boolean): UseQueryResult<FailureGroups, ApiError> {
  return useQuery<FailureGroups, ApiError>({
    queryKey: [...MESSAGE_LOG_KEY, 'causes'],
    queryFn: () => apiGet<FailureGroups>('/notifications/messages/causes'),
    placeholderData: (previous) => previous,
    refetchInterval: REFRESH_MS,
    enabled,
  });
}

export function useMessageDetail(id: string | undefined, enabled: boolean): UseQueryResult<MessageDeliveryDetail, ApiError> {
  return useQuery<MessageDeliveryDetail, ApiError>({
    queryKey: [...MESSAGE_LOG_KEY, 'detail', id ?? null],
    queryFn: () => apiGet<MessageDeliveryDetail>(`/notifications/messages/${id ?? ''}`),
    enabled: enabled && id !== undefined,
    retry: false,
  });
}

type Snapshot = readonly (readonly [QueryKey, unknown])[];

/**
 * Act first (non-negotiable #11): the email shows its new state at once, everywhere it is on screen; a refusal puts
 * it back and the page says why.
 */
async function patchMessage(queryClient: QueryClient, id: string, patch: Partial<MessageDeliveryRow>): Promise<Snapshot> {
  await queryClient.cancelQueries({ queryKey: MESSAGE_LOG_KEY });
  const snapshot = queryClient.getQueriesData({ queryKey: MESSAGE_LOG_KEY });
  queryClient.setQueriesData<MessageLogPage>({ queryKey: [...MESSAGE_LOG_KEY, 'list'] }, (page) =>
    page ? { ...page, rows: page.rows.map((row) => (row.id === id ? { ...row, ...patch } : row)) } : page,
  );
  queryClient.setQueryData<MessageDeliveryDetail>([...MESSAGE_LOG_KEY, 'detail', id], (detail) => (detail ? { ...detail, ...patch } : detail));
  return snapshot;
}

function restore(queryClient: QueryClient, snapshot: Snapshot | undefined): void {
  for (const [key, data] of snapshot ?? []) queryClient.setQueryData(key, data);
}

/** Held until the refetch lands, so nothing jumps back and forth (ui-instant-feel rule 3). */
async function refresh(queryClient: QueryClient): Promise<void> {
  await Promise.all([queryClient.invalidateQueries({ queryKey: MESSAGE_LOG_KEY }), queryClient.invalidateQueries({ queryKey: ADMIN_MENU_COUNTS_KEY })]);
}

function messageAction<TInput, TResult>(
  run: (input: TInput) => Promise<TResult>,
  idOf: (input: TInput) => string,
  becomes: MessageDeliveryState,
) {
  return function useAction() {
    const queryClient = useQueryClient();
    return useMutation<TResult, ApiError, TInput, Snapshot>({
      mutationFn: run,
      onMutate: (input) => patchMessage(queryClient, idOf(input), { state: becomes }),
      onError: (_error, _input, snapshot) => restore(queryClient, snapshot),
      onSettled: () => refresh(queryClient),
    });
  };
}

/**
 * Send again (owner Q4): asked beside its button; no Undo, because the email goes. Back in the queue with its earlier
 * tries counted, so the server calls it "Still trying" until the next try lands; the page says the same at once.
 */
export const useResendMessage = messageAction(
  (id: string) => apiPost<MessageDeliveryRow>(`/notifications/messages/${id}/resend`),
  (id) => id,
  'retrying',
);

/** Fix address: a fresh copy to the corrected address. The person's own record is never changed. */
export const useReaddressMessage = messageAction(
  (input: { readonly id: string; readonly toAddress: string }) => apiPost<ReaddressResult>(`/notifications/messages/${input.id}/readdress`, { toAddress: input.toAddress }),
  (input) => input.id,
  'replaced',
);

/** Dismiss: "nobody will send this again". */
export const useDismissMessage = messageAction(
  (id: string) => apiPost<MessageDeliveryRow>(`/notifications/messages/${id}/dismiss`),
  (id) => id,
  'dismissed',
);

/** Send all again: the server decides email by email and says what it skipped, so this waits for its answer. */
export function useResendAll() {
  const queryClient = useQueryClient();
  return useMutation<ResendAllResult, ApiError, { readonly cause?: FailureCause }>({
    mutationFn: (input) => apiPost<ResendAllResult>('/notifications/messages/resend-all', input),
    onSettled: () => refresh(queryClient),
  });
}
